import {Component, EventEmitter, inject, OnInit, Output, signal} from '@angular/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {FormBuilder, FormGroup, UntypedFormBuilder, Validators} from '@angular/forms';
import {DcpValidators} from '@app/services/dcp-validators';
import {take} from 'rxjs/operators';
import {Router} from '@angular/router';
import {ProductDetailsPageHistoryService} from '@app/services/user-registration/product-details-page-history.service';
import {UserNameValidationService} from '@app/services/user-registration/user-name-validation.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TranslationService} from '@spartacus/core';
import {Observable} from 'rxjs';

interface FieldState {
  errorState: 'Standard' | 'Error';
  message: null | string;
}

@UntilDestroy()
@Component({
  selector: 'dcp-customer-name',
  templateUrl: './customer-name.component.html',
  styleUrls: ['./customer-name.component.scss']
})
export class CustomerNameComponent implements OnInit {

  @Output() nameValidated = new EventEmitter<boolean>();
  private fb = inject(FormBuilder)
  private translationService = inject(TranslationService)
  public userAccountFacade= inject(UserAccountFacade)
  private formBuilder= inject(UntypedFormBuilder)
  private dcpValidators= inject(DcpValidators)
  private router= inject(Router)
  private pdpHistoryService= inject(ProductDetailsPageHistoryService)
  private userNameValidationService= inject(UserNameValidationService)

  form: FormGroup;
  firstName = signal<FieldState>({errorState: 'Standard', message: null});
  lastName = signal<FieldState>({errorState: 'Standard', message: null});

  ngOnInit(): void {
    this.form = this.fb.group(
      {
        firstName: this.formBuilder.control(
          '',
          [
            Validators.required,
            Validators.maxLength(40),
            DcpValidators.onlyLatin1Characters(),
            this.dcpValidators.noForbiddenWords()
          ]
        ),
        lastName: this.formBuilder.control(
          '',
          [
            Validators.required,
            Validators.maxLength(40),
            DcpValidators.onlyLatin1Characters(),
            this.dcpValidators.noForbiddenWords()
          ]
        )
      },
      {
        validators: [
          DcpValidators.checkMatch('firstName', 'lastName'),
        ]
      }
    );

    this.userAccountFacade.get().pipe(take(1), untilDestroyed(this)).subscribe((user) => {
      this.form.get('firstName').patchValue(user.firstName);
      this.form.get('lastName').patchValue(user.lastName);
    });
  }

  onInput(event: CustomEvent, field: string): void {
    const control = this.form.get(field);
    const target = event.target as HTMLInputElement;
    control.setValue(target.value);
    this.updateFieldState(field);
  }

  updateFieldState(fieldName: string): void {
    const errorMessage$ = this.getErrorMessage(fieldName);

    errorMessage$
      .pipe(untilDestroyed(this))
      .subscribe(message => {
        if (fieldName === 'firstName') {
          this.firstName.set({
            errorState: message ? 'Error' : 'Standard',
            message: message
          });
        } else if (fieldName === 'lastName') {
          this.lastName.set({
            errorState: message ? 'Error' : 'Standard',
            message: message
          });
        }
      });
  }

  getErrorMessage(fieldName: string): Observable<string | null> {
    return new Observable<string | null>(observer => {
      const control = this.form.get(fieldName);

      if (fieldName === 'lastName' && this.form.hasError('checkMatch-firstName:lastName')) {
        this.translationService.translate('registration.error.duplicatesNames')
          .pipe(untilDestroyed(this))
          .subscribe(message => {
            observer.next(message);
            observer.complete();
          });
        return;
      }

      if (!control || !control.errors) {
        observer.next(null);
        observer.complete();
        return;
      }

      let errorMessage: Observable<string> | null = null;

      if (control.errors['required']) {
        errorMessage = this.translationService.translate('registration.error.required');
      } else if (control.errors['maxlength']) {
        errorMessage = this.translationService.translate('registration.error.maxLength', {maxLength: 40});
      } else if (control.errors['onlyLatin1Characters']) {
        errorMessage = this.translationService.translate('registration.error.nonLatin');
      } else if (control.errors['forbiddenWords']) {
        errorMessage = this.translationService.translate('registration.error.forbiddenWords');
      }

      if (errorMessage) {
        errorMessage
          .pipe(untilDestroyed(this))
          .subscribe(message => {
          observer.next(message);
          observer.complete();
        });
      } else {
        observer.next(null);
        observer.complete();
      }
    });
  }

  onSubmit() {
    if (this.form.invalid) {
      return;
    }

    this.form.disable();
    this.userNameValidationService.setValidUserName(this.form.get('firstName').value, this.form.get('lastName').value);
    this.nameValidated.emit(true);
  }

  goBack() {
    this.router.navigateByUrl(this.pdpHistoryService.getCurrent() || '/');
    this.nameValidated.emit(false);
  }
}
